import type { Locale } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import frCA from 'date-fns/locale/fr-CA';
import es from 'date-fns/locale/es';

import type { NzI18nInterface } from 'ng-zorro-antd/i18n';
import { en_US, fr_FR, es_ES } from 'ng-zorro-antd/i18n';

import localesMeta from './supported-languages.json';
import { CookieKey } from '../cookies/cookie-keys.const';

/**
 * Full language metadata (single source for compile-time keys). `LanguageCode` is derived from
 * these keys. `locales.config.json` is validated against the same keys at module load.
 */
const LANGUAGE_CONFIGS = {
  'en-US': {
    code: 'en-US',
    name: 'English (US)',
    keyNamespace: 'ENGLISH',
    momentLocale: 'en',
    dateFnsLocale: enUS,
    nzI18n: en_US,
    adaChatbotLocale: 'en',
    helpCenterLocale: 'en-us',
    thoughtSpotLocale: 'en-US',
    bulkEditsLocale: 'en',
  },
  'fr-CA': {
    code: 'fr-CA',
    name: 'French (CA)',
    keyNamespace: 'FRENCH',
    momentLocale: localesMeta.momentLocaleMap['fr-CA'],
    dateFnsLocale: frCA,
    nzI18n: fr_FR,
    adaChatbotLocale: 'fr',
    helpCenterLocale: 'fr-ca',
    thoughtSpotLocale: 'fr-CA',
    bulkEditsLocale: 'fr',
  },
  'es-MX': {
    code: 'es-MX',
    name: 'Spanish (MX)',
    keyNamespace: 'SPANISH',
    momentLocale: localesMeta.momentLocaleMap['es-MX'],
    dateFnsLocale: es,
    nzI18n: es_ES,
    adaChatbotLocale: 'es',
    helpCenterLocale: 'es-mx',
    thoughtSpotLocale: 'es-MX',
    bulkEditsLocale: 'es',
  },
} as const;

export type LanguageCode = keyof typeof LANGUAGE_CONFIGS;
export type LanguageKeyNamespace = typeof LANGUAGE_CONFIGS[LanguageCode]['keyNamespace'];

export interface LanguageConfig {
  code: LanguageCode;
  name: string;
  keyNamespace: string;
  momentLocale: string;
  dateFnsLocale: Locale;
  nzI18n: NzI18nInterface;
  adaChatbotLocale: string;
  helpCenterLocale: string;
  thoughtSpotLocale: string;
  bulkEditsLocale: string;
}

/**
 * @deprecated Once `LocalizationService` (app/locale-formatters) is fully onboarded and all
 * usages of Angular's built-in formatting pipes (DatePipe, DecimalPipe, CurrencyPipe,
 * PercentPipe) and ng-zorro's locale infrastructure are migrated to date-fns, this map and the
 * `registerLocaleData` call in utils.ts can be removed entirely.
 * Until then, each entry must be kept in sync with LANGUAGE_CONFIGS.
 */
export const ANGULAR_LOCALE_IMPORTS: Partial<Record<LanguageCode, () => Promise<{ default: unknown }>>> = {
  'fr-CA': () => import('@angular/common/locales/fr-CA'),
  'es-MX': () => import('@angular/common/locales/es-MX'),
};

const languageCodesFromJson = new Set(localesMeta.supportedLanguages);
const languageCodesFromMap = new Set(Object.keys(LANGUAGE_CONFIGS));

if (
  languageCodesFromJson.size !== languageCodesFromMap.size ||
  [...languageCodesFromJson].some((c) => !languageCodesFromMap.has(c as LanguageCode))
) {
  throw new Error(
    `Supported languages mismatch: ${[...languageCodesFromJson].join(', ')} vs ${[...languageCodesFromMap].join(', ')}`,
  );
}

if (!(LANGUAGE_CONFIGS[localesMeta.sourceLanguage as LanguageCode])) {
  throw new Error(
    `Source language ${localesMeta.sourceLanguage} not found in LANGUAGE_CONFIGS`,
  );
}

export const DEFAULT_LANGUAGE: LanguageCode = LANGUAGE_CONFIGS[localesMeta.sourceLanguage as LanguageCode].code;
export const DEFAULT_LANGUAGE_CONFIG: LanguageConfig = LANGUAGE_CONFIGS[DEFAULT_LANGUAGE];


export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return LANGUAGE_CONFIGS[code as LanguageCode] ?? undefined;
}


export const I18N_COOKIE_KEY: CookieKey = CookieKey.I18N_LANG;

/**
 * Normalizes raw `i18n_lang` cookie values to canonical `LanguageCode`.
 * - Legacy shorthand: only `en` maps to `en-US`.
 * - All supported languages also map 1:1 by BCP-47 code (e.g. `en-GB` → `en-GB`).
 * Needs to be maintained in locale-detection.ejs as well
 */
export const I18N_LANG_COOKIE_MAP: Record<string, string> = {
  en: 'en-US',
  ...Object.fromEntries(Object.keys(LANGUAGE_CONFIGS).map((key) => [key, key])),
};

export const SUPPORTED_LANGUAGES: Record<LanguageCode, string> = Object.keys(LANGUAGE_CONFIGS).reduce((acc, key) => {
  acc[key as LanguageCode] = LANGUAGE_CONFIGS[key as LanguageCode].name;
  return acc;
}, {} as Record<LanguageCode, string>);

/**
 * @deprecated Prefer `SupportedLocale` from the app locale formatting layer (`locale.types`,
 * `LocalizationService`). This enum is kept for compatibility during migration; new and updated
 * code should use the `SupportedLocale` mapping used by the locale formatting service instead of
 * `LOCALES`.
 */
export enum LOCALES {
  ENGLISH = 'en-US',
  FRENCH = 'fr-CA',
  SPANISH = 'es-MX',
}
